<template>
    <div class="login-page">
        <div ref="languageBox" class="page-actions">
            <button
                type="button"
                class="language-btn"
                @click.stop="toggleLanguageMenu"
            >
                <span>{{ currentLanguageLabel }}</span>
                <span class="language-arrow" :class="{ open: showLanguagePopover }"></span>
            </button>

            <div v-if="showLanguagePopover" class="language-menu">
                <button
                    v-for="item in languageActions"
                    :key="item.value"
                    type="button"
                    class="language-item"
                    :class="{ active: item.value === currentLanguageValue }"
                    @click.stop="changeLang(item)"
                >
                    <span>{{ item.text }}</span>
                    <van-icon v-if="item.value === currentLanguageValue" name="success" />
                </button>
            </div>
        </div>

        <main class="auth-page">
            <section class="auth-card">
                <div class="eyebrow">{{ getLabel('login.brandTag', 'VAST GPU NETWORK') }}</div>
                <h1 class="title">{{ getLabel('new_lang.welcome_back', '欢迎回来') }}</h1>
                <p class="subtitle">{{ getLabel('login.subtitle', '欢迎访问 Vast.ai 算力基础设施底座') }}</p>

                <form class="auth-form" @submit.prevent="submit">
                    <div class="field">
                        <label>{{ getLabel('login.phoneNumber', 'Phone Number') }}</label>
                        <div class="input-wrap">
                            <input
                                v-model.trim="data.username"
                                type="text"
                                autocomplete="username"
                                autocapitalize="none"
                                spellcheck="false"
                                :placeholder="getLabel('login.emailPlaceholder', 'Enter your email')"
                            >
                        </div>
                    </div>

                    <div class="field">
                        <label>{{ getLabel('login.passwordLabel', '登录密码') }}</label>
                        <div class="input-wrap">
                            <input
                                v-model.trim="data.password"
                                :type="passwordVisible ? 'text' : 'password'"
                                autocomplete="current-password"
                                autocapitalize="none"
                                spellcheck="false"
                                :placeholder="getLabel('login.password', '请输入密码')"
                            >
                            <button
                                type="button"
                                class="eye-btn"
                                :aria-label="passwordVisible ? getLabel('login.hidePassword', '隐藏密码') : getLabel('login.showPassword', '显示密码')"
                                @click="togglePassword"
                            >
                                <van-icon :name="passwordVisible ? 'eye-o' : 'closed-eye'" />
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        class="submit-btn"
                        :disabled="submitLoading || !data.username || !data.password"
                    >
                        {{ submitLoading ? getLabel('utils.loading', '登录中...') : getLabel('login.signInBtn', '立即登录') }}
                    </button>
                </form>

                <div class="auth-switch">
                    {{ getLabel('login.noAccount', '没有账号？') }}
                    <router-link to="/register">{{ getLabel('login.signUp', '立即注册') }}</router-link>
                </div>
            </section>

            <div class="copyright">{{ getLabel('login.copyright', '© 2026 Vast.ai Compute Network') }}</div>
        </main>

        <button
            type="button"
            class="service-float"
            :aria-label="getLabel('user.onlineService', '在线客服')"
            @click="kefuTo"
        >
            <van-icon name="service-o" />
        </button>
    </div>
</template>

<script>
import Vue from "vue";
import Fetch from "../../utils/fetch";
import { Icon, Toast } from "vant";

Vue.use(Icon);
Vue.use(Toast);

export default {
    name: "login",
    data() {
        return {
            passwordVisible: false,
            submitLoading: false,
            showLanguagePopover: false,
            appOriginalStyles: null,
            bodyOriginalBackground: "",
            data: {
                username: "",
                password: ""
            },
        };
    },
    computed: {
        languageActions() {
            return [

                { text: this.getLabel("lang.en_us", "English"), value: "en_us" },
            ];
        },
        currentLanguageValue() {
            return this.$i18n.locale || "en_us";
        },
        currentLanguageLabel() {
            const currentItem = this.languageActions.find((item) => item.value === this.currentLanguageValue);
            return currentItem ? currentItem.text : this.getLabel("safe.language", "语言");
        },
    },
    created() {
        this.data.username = localStorage.getItem("login_username") || "";
        this.data.password = localStorage.getItem("login_password") || "";
        this.$parent.footer("user", false);
    },
    mounted() {
        this.applyFullScreenLayout();
        document.addEventListener("click", this.handleDocumentClick);
    },
    beforeDestroy() {
        this.restoreFullScreenLayout();
        document.removeEventListener("click", this.handleDocumentClick);
    },
    methods: {
        getLabel(key, defaultText) {
            return this.$te(key) ? this.$t(key) : defaultText;
        },
        applyFullScreenLayout() {
            const app = document.getElementById("app");
            if (!app) {
                return;
            }

            this.appOriginalStyles = {
                maxWidth: app.style.maxWidth,
                width: app.style.width,
                margin: app.style.margin,
                backgroundColor: app.style.backgroundColor,
                paddingBottom: app.style.paddingBottom,
            };
            this.bodyOriginalBackground = document.body.style.background;

            app.style.maxWidth = "100%";
            app.style.width = "100%";
            app.style.margin = "0";
            app.style.backgroundColor = "#02050b";
            app.style.paddingBottom = "0";
            document.body.style.background = "#02050b";
        },
        restoreFullScreenLayout() {
            const app = document.getElementById("app");
            if (app && this.appOriginalStyles) {
                app.style.maxWidth = this.appOriginalStyles.maxWidth || "";
                app.style.width = this.appOriginalStyles.width || "";
                app.style.margin = this.appOriginalStyles.margin || "";
                app.style.backgroundColor = this.appOriginalStyles.backgroundColor || "";
                app.style.paddingBottom = this.appOriginalStyles.paddingBottom || "";
            }
            document.body.style.background = this.bodyOriginalBackground || "";
        },
        toggleLanguageMenu() {
            this.showLanguagePopover = !this.showLanguagePopover;
        },
        handleDocumentClick(event) {
            const languageBox = this.$refs.languageBox;
            if (!languageBox) {
                return;
            }
            if (!languageBox.contains(event.target)) {
                this.showLanguagePopover = false;
            }
        },
        togglePassword() {
            this.passwordVisible = !this.passwordVisible;
        },
        changeLang(action) {
            const lang = action.value;
            if (!lang) {
                return;
            }
            this.$i18n.locale = lang;
            localStorage.setItem("lang", lang);
            this.showLanguagePopover = false;
        },
        kefuTo() {
            this.$router.push("/kefu");
        },
        async submit() {
            if (this.submitLoading) {
                return;
            }

            if (!this.data.username) {
                this.$toast({
                    className: "toastName",
                    message: this.getLabel("login.phoneEmpty", "Enter your account / mobile phone number"),
                });
                return;
            }

            if (!this.data.password) {
                this.$toast({
                    className: "toastName",
                    message: this.getLabel("login.passwordEmpty", "Enter your login password"),
                });
                return;
            }

            this.submitLoading = true;

            try {
                localStorage.setItem("login_username", this.data.username);
                localStorage.setItem("login_password", this.data.password);

                const res = await Fetch("/index/login", {
                    username: this.data.username,
                    password: this.data.password,
                });

                if (res.data.token) {
                    localStorage.setItem("token", res.data.token);
                }

                this.$toast({
                    className: "toastName",
                    message: this.getLabel("login.signInSuccess", "登录成功"),
                });

                this.$router.replace({
                    path: this.$router.history.current.query.redirect || "/",
                });
            } catch (error) {
                // Fetch 内部已经统一处理错误弹窗，这里只负责结束按钮状态
            } finally {
                this.submitLoading = false;
            }
        },
    },
};
</script>

<style lang="less" scoped>
.login-page {
    min-height: 100vh;
    background:
        radial-gradient(circle at 78% 13%, rgba(24, 75, 255, 0.42), transparent 31%),
        radial-gradient(circle at 14% 52%, rgba(92, 42, 255, 0.17), transparent 26%),
        linear-gradient(180deg, #030815, #02050b 72%);
}

.page-actions {
    position: fixed;
    top: 18px;
    right: max(18px, calc((100vw - 430px) / 2 + 18px));
    z-index: 90;
}

.language-btn {
    min-width: 136px;
    height: 48px;
    padding: 0 18px;
    border: 1px solid rgba(94, 132, 230, 0.42);
    border-radius: 24px;
    background: rgba(10, 22, 45, 0.88);
    color: #dbe5f7;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.28);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
}

.language-arrow {
    width: 8px;
    height: 8px;
    border-right: 2px solid #6b94ff;
    border-bottom: 2px solid #6b94ff;
    transform: rotate(45deg) translateY(-1px);
    transition: transform 0.2s ease;
}

.language-arrow.open {
    transform: rotate(-135deg) translateY(-1px);
}

.language-menu {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    width: 188px;
    max-height: 280px;
    padding: 8px;
    border: 1px solid rgba(94, 132, 230, 0.32);
    border-radius: 18px;
    background: rgba(9, 20, 42, 0.98);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
    overflow-y: auto;
    backdrop-filter: blur(10px);
}

.language-menu::-webkit-scrollbar {
    width: 6px;
}

.language-menu::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: rgba(107, 148, 255, 0.35);
}

.language-item {
    width: 100%;
    min-height: 42px;
    padding: 0 12px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    color: #dbe5f7;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 16px;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
}

.language-item + .language-item {
    margin-top: 4px;
}

.language-item.active {
    background: rgba(49, 94, 255, 0.18);
    color: #ffffff;
}

.language-item .van-icon {
    color: #6b94ff;
    font-size: 18px;
}

.language-item:active {
    background: rgba(49, 94, 255, 0.12);
}

.auth-page {
    width: 100%;
    max-width: 430px;
    min-height: 100vh;
    margin: 0 auto;
    padding: 76px 18px 120px;
    box-sizing: border-box;
}

.auth-card {
    position: relative;
    padding: 42px 28px 30px;
    border-radius: 24px;
    background: linear-gradient(155deg, rgba(14, 31, 61, 0.98), rgba(7, 20, 42, 0.98));
    border: 1px solid rgba(68, 120, 221, 0.36);
    box-shadow: 0 24px 65px rgba(0, 0, 0, 0.46), inset 0 0 42px rgba(28, 74, 170, 0.08);
}

.auth-card::before {
    content: "";
    position: absolute;
    left: 8%;
    right: 8%;
    top: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #3478ff, transparent);
    box-shadow: 0 0 18px #1765ff;
}

.eyebrow {
    color: #6b94ff;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
}

.title {
    margin: 10px 0 8px;
    color: #ffffff;
    font-size: 34px;
    line-height: 1.15;
    font-weight: 700;
}

.subtitle {
    margin: 0 0 34px;
    color: #8795af;
    font-size: 14px;
    line-height: 1.7;
}

.field {
    margin-bottom: 22px;

    label {
        display: block;
        margin-bottom: 10px;
        color: #dbe5f7;
        font-size: 14px;
        font-weight: 700;
    }
}

.input-wrap {
    position: relative;

    input {
        width: 100%;
        height: 72px;
        padding: 0 54px 0 17px;
        border: 1px solid rgba(79, 111, 166, 0.25);
        border-radius: 16px;
        outline: none;
        background: #1b2940;
        color: #ffffff;
        font-size: 20px;
        box-sizing: border-box;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.025);
        transition: border-color 0.2s, box-shadow 0.2s;
    }

    input:focus {
        border-color: #3276ff;
        box-shadow: 0 0 0 3px rgba(50, 118, 255, 0.13), 0 8px 24px rgba(13, 72, 200, 0.14);
    }

    input::placeholder {
        color: #71809b;
        font-size: 20px;
    }
}

.eye-btn {
    position: absolute;
    right: 14px;
    top: 50%;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #8b9ab4;
    transform: translateY(-50%);
    cursor: pointer;

    .van-icon {
        font-size: 24px;
    }
}

.submit-btn {
    width: 100%;
    height: 74px;
    margin-top: 8px;
    border: 0;
    border-radius: 17px;
    background: linear-gradient(90deg, #315eff, #247bff);
    color: #ffffff;
    font-size: 28px;
    font-weight: 800;
    box-shadow: 0 12px 30px rgba(35, 94, 255, 0.32);
    cursor: pointer;
    transition: transform 0.12s ease, filter 0.12s ease, opacity 0.12s ease;
}

.submit-btn:active {
    transform: scale(0.985);
    filter: brightness(0.92);
}

.submit-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.auth-switch {
    margin-top: 27px;
    text-align: center;
    color: #8795af;
    font-size: 14px;

    a {
        color: #4e88ff;
        text-decoration: none;
        font-weight: 800;
    }
}

.copyright {
    margin-top: 55px;
    text-align: center;
    color: #465167;
    font-size: 12px;
}

.service-float {
    position: fixed;
    right: max(18px, calc((100vw - 430px) / 2 + 18px));
    bottom: max(28px, env(safe-area-inset-bottom));
    z-index: 80;
    width: 64px;
    height: 64px;
    border: 1px solid rgba(156, 190, 255, 0.58);
    border-radius: 50%;
    background: linear-gradient(145deg, #337cff, #5939f3);
    color: #ffffff;
    box-shadow: 0 0 0 6px rgba(45, 91, 255, 0.1), 0 9px 24px rgba(21, 75, 255, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    .van-icon {
        font-size: 34px;
    }
}

@media (max-width: 360px) {
    .page-actions {
        right: 12px;
    }

    .language-btn {
        min-width: 116px;
        padding: 0 14px;
        font-size: 14px;
    }

    .language-menu {
        width: 168px;
    }

    .auth-page {
        padding-left: 12px;
        padding-right: 12px;
    }

    .auth-card {
        padding-left: 20px;
        padding-right: 20px;
    }

    .title {
        font-size: 30px;
    }

    .input-wrap input,
    .input-wrap input::placeholder {
        font-size: 18px;
    }
}
</style>
